import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { supabaseSecure, USE_SECURE_COOKIES, secureAuth } from "@/lib/secureClient";

interface AuthResult {
  success: boolean;
  error?: string;
  retryAfter?: number;
  user?: any;
  session?: any;
}

interface RateLimitState {
  remaining: number | null;
  resetTime: number | null;
  isLocked: boolean;
}

// Use the appropriate client based on environment
const client = USE_SECURE_COOKIES ? supabaseSecure : supabase;

export function useRateLimitedAuth() {
  const [rateLimitState, setRateLimitState] = useState<RateLimitState>({
    remaining: null,
    resetTime: null,
    isLocked: false,
  });

  const signIn = useCallback(async (email: string, password: string): Promise<AuthResult> => {
    try {
      // On production (secure cookies enabled), use auth-proxy for httpOnly cookie auth
      if (USE_SECURE_COOKIES) {
        const result = await secureAuth.signIn(email, password);
        
        if (result.error) {
          // Handle rate limiting from auth-proxy
          const errorMsg = typeof result.error === 'object' && 'message' in result.error 
            ? result.error.message 
            : String(result.error);
            
          if (errorMsg.includes("429") || errorMsg.includes("Too many")) {
            setRateLimitState({
              remaining: 0,
              resetTime: Date.now() + 30 * 60 * 1000,
              isLocked: true,
            });
            return {
              success: false,
              error: "Too many login attempts. Please try again later.",
            };
          }
          return { success: false, error: errorMsg };
        }

        setRateLimitState({ remaining: null, resetTime: null, isLocked: false });
        return { success: true, user: result.data?.user, session: result.data?.session };
      }

      // On development/preview, use rate-limited-auth edge function with localStorage
      const { data, error } = await client.functions.invoke("rate-limited-auth", {
        body: { action: "signin", email, password },
      });

      if (error) {
        // Try to extract the actual error message from the response body
        let errorMsg = error.message || "Authentication failed";
        try {
          if (error.context && typeof error.context.json === 'function') {
            const body = await error.context.json();
            if (body?.error) errorMsg = body.error;
            if (body?.retryAfter) {
              setRateLimitState({
                remaining: 0,
                resetTime: Date.now() + body.retryAfter * 1000,
                isLocked: true,
              });
              return { success: false, error: errorMsg, retryAfter: body.retryAfter };
            }
          }
        } catch {
          // Failed to parse response body, use original error
        }

        // Check for rate limit error (429)
        if (errorMsg.includes("429") || errorMsg.includes("Too many")) {
          setRateLimitState({
            remaining: 0,
            resetTime: Date.now() + 30 * 60 * 1000,
            isLocked: true,
          });
          return {
            success: false,
            error: "Too many login attempts. Please try again later.",
          };
        }
        return { success: false, error: errorMsg };
      }

      if (data?.error) {
        // Handle rate limit from response
        if (data.retryAfter) {
          setRateLimitState({
            remaining: 0,
            resetTime: Date.now() + data.retryAfter * 1000,
            isLocked: true,
          });
        }
        return { success: false, error: data.error, retryAfter: data.retryAfter };
      }

      // Successful login - set the session using the SAME client as useAuth
      // This ensures the onAuthStateChange listener fires correctly
      if (data?.session) {
        try {
          // Validate token format before setting (basic structure check)
          if (!data.session.access_token || typeof data.session.access_token !== 'string' ||
              !data.session.refresh_token || typeof data.session.refresh_token !== 'string') {
            throw new Error('Invalid session token format');
          }
          
          console.log('[auth] Setting session with client...');
          
          // Use the same client that useAuth listens to
          const { error: sessionError } = await client.auth.setSession({
            access_token: data.session.access_token,
            refresh_token: data.session.refresh_token,
          });
          
          if (sessionError) {
            console.error('[auth] Session validation failed:', sessionError.message);
            return { success: false, error: 'Session validation failed. Please try again.' };
          }
          
          // Verify session was set correctly
          const { data: verifiedSession } = await client.auth.getSession();
          console.log('[auth] Session verified:', !!verifiedSession?.session);
          
          if (!verifiedSession?.session) {
            console.error('[auth] Session verification failed after setting');
            return { success: false, error: 'Session verification failed. Please try again.' };
          }
        } catch (sessionErr) {
          console.error('[auth] Error setting session:', sessionErr);
          return { success: false, error: 'Failed to establish session. Please try again.' };
        }
      }

      setRateLimitState({ remaining: null, resetTime: null, isLocked: false });
      return { success: true, user: data.user, session: data.session };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Authentication failed";
      return { success: false, error: errorMessage };
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string, name: string): Promise<AuthResult> => {
    try {
      // On production (secure cookies enabled), use auth-proxy for httpOnly cookie auth
      if (USE_SECURE_COOKIES) {
        const result = await secureAuth.signUp(email, password, name);
        
        if (result.error) {
          const errorMsg = typeof result.error === 'object' && 'message' in result.error 
            ? result.error.message 
            : String(result.error);
            
          if (errorMsg.includes("429") || errorMsg.includes("Too many")) {
            setRateLimitState({
              remaining: 0,
              resetTime: Date.now() + 30 * 60 * 1000,
              isLocked: true,
            });
            return {
              success: false,
              error: "Too many signup attempts. Please try again later.",
            };
          }
          return { success: false, error: errorMsg };
        }

        setRateLimitState({ remaining: null, resetTime: null, isLocked: false });
        return { success: true, user: result.data?.user };
      }

      // On development/preview, use rate-limited-auth edge function
      const { data, error } = await client.functions.invoke("rate-limited-auth", {
        body: { 
          action: "signup", 
          email, 
          password, 
          name,
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) {
        // Surface the server's real reason (e.g. email already registered)
        try {
          const ctx = (error as { context?: Response }).context;
          if (ctx && typeof ctx.json === "function") {
            const body = await ctx.json();
            if (body?.error) {
              if (body.retryAfter) {
                setRateLimitState({
                  remaining: 0,
                  resetTime: Date.now() + body.retryAfter * 1000,
                  isLocked: true,
                });
              }
              return { success: false, error: body.error, retryAfter: body.retryAfter };
            }
          }
        } catch {
          // fall through to generic handling
        }
        if (error.message?.includes("429") || error.message?.includes("Too many")) {
          setRateLimitState({
            remaining: 0,
            resetTime: Date.now() + 30 * 60 * 1000,
            isLocked: true,
          });
          return {
            success: false,
            error: "Too many signup attempts. Please try again later.",
          };
        }
        return { success: false, error: error.message };
      }

      if (data?.error) {
        if (data.retryAfter) {
          setRateLimitState({
            remaining: 0,
            resetTime: Date.now() + data.retryAfter * 1000,
            isLocked: true,
          });
        }
        return { success: false, error: data.error, retryAfter: data.retryAfter };
      }

      setRateLimitState({ remaining: null, resetTime: null, isLocked: false });
      return { success: true, user: data.user };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Signup failed";
      return { success: false, error: errorMessage };
    }
  }, []);

  return {
    signIn,
    signUp,
    rateLimitState,
    isRateLimited: rateLimitState.isLocked,
  };
}
